import React, { useEffect, useState, useCallback } from "react";
import style from "./Formulary.module.scss";
import { Modal } from "../../../secondary";
import { useDispatch, useSelector } from "react-redux";
import {
  getFormularyFacilities,
  getProductDivision,
  getProductMasterMaterialByIdAsync,
  searchByProductDivision,
  setSelectedFacility,
} from "../../../../app/homeSlice";
import { useLoading } from "../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../constants/loadingKeys";
import { ProductDivisionChips } from "./ProductDivisionChips";
import { CategoryAccordion } from "./CategoryAccordion";
import { MaterialDetailView } from "../Products/MaterialDetailView";
import { dateOfBirthDate } from "../../../../utils";
import { ProductDetailSkeleton } from "../Products/ProductDetailSkeleton";
import { useRequestMoreInfo } from "../hooks/useRequestMoreInfo";
import { FacilitySelect } from "../../../secondary/FacilitySelect/FacilitySelect";

const VIEW = { LIST: "list", DETAIL: "detail" };

export const Formulary = ({ modalOpen, onClose }) => {
  const dispatch = useDispatch();

  const customerNumber = useSelector((s) => s.user?.customerNumber);
  const productDivision = useSelector((s) => s.home.productDivision);
  const formularyFacilities = useSelector((s) => s.home.formularyFacilities);
  const selectedFacility = useSelector((s) => s.home.selectedFacility);
  const formularyId = selectedFacility?.forumaryId;

  const facilitiesLoading = useLoading(LOADING_KEYS.GET_FORMULARY_FACILITIES);
  const searchLoading = useLoading(LOADING_KEYS.SEARCH_BY_PRODUCT_DIVISION);
  const materialLoading = useLoading(LOADING_KEYS.GET_PRODUCT_MASTER_MATERIAL_BY_ID);

  const [view, setView] = useState(VIEW.LIST);
  const [selectedChipKeys, setSelectedChipKeys] = useState([]);
  const [productCategoryData, setProductCategoryData] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);

  const { moreInfoLoading, reqSuccessMessage, setReqSuccessMessage, handleRequestMoreInfo } = useRequestMoreInfo({
    selectedMaterialId,
  });

  useEffect(() => {
    if (customerNumber) dispatch(getFormularyFacilities(customerNumber));
  }, [customerNumber, dispatch]);

  useEffect(() => {
    if (!formularyId) return;

    setSelectedChipKeys([]);
    setProductCategoryData(null);
    setExpandedKeys([]);
    setView(VIEW.LIST);
    setSelectedMaterial(null);
    setReqSuccessMessage(null);

    dispatch(getProductDivision(formularyId));

    let cancelled = false;
    (async () => {
      const data = await dispatch(searchByProductDivision(formularyId, { productDivisionIds: [] }));
      if (cancelled) return;
      setProductCategoryData(data);
      const firstCategory = data?.productCategory?.[0]?.productCategoryDesc;
      setExpandedKeys(firstCategory ? [firstCategory] : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [formularyId, dispatch, setReqSuccessMessage]);

  const runSearch = useCallback(
    async (chipKeys) => {
      const data = await dispatch(searchByProductDivision(formularyId, { productDivisionIds: chipKeys }));
      setProductCategoryData(data);
      const categories = data?.productCategory ?? [];
      setExpandedKeys((prev) => {
        const stillValid = prev.filter((k) => categories.some((c) => c.productCategoryDesc === k));
        if (stillValid.length > 0) return stillValid;
        return categories[0] ? [categories[0].productCategoryDesc] : [];
      });
    },
    [dispatch, formularyId]
  );

  const handleFacilityChange = (facility) => {
    dispatch(setSelectedFacility(facility));
  };

  const handleChipToggle = (key) => {
    setSelectedChipKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      runSearch(next);
      return next;
    });
  };

  const handleAccordionToggle = (categoryKey) => {
    setExpandedKeys((prev) => (prev.includes(categoryKey) ? prev.filter((k) => k !== categoryKey) : [...prev, categoryKey]));
  };

  const handleCardClick = async (materialId) => {
    setSelectedMaterialId(materialId);
    setSelectedMaterial(null);
    setReqSuccessMessage(null);
    setView(VIEW.DETAIL);

    const data = await dispatch(getProductMasterMaterialByIdAsync(materialId, selectedFacility?.facilityNumber));

    if (data) {
      setSelectedMaterial(data);
    }
  };

  const handleBack = () => {
    setView(VIEW.LIST);
    setSelectedMaterial(null);
    setReqSuccessMessage(null);
  };

  const categories = productCategoryData?.productCategory ?? [];

  return (
    <Modal modalOpen={modalOpen} modalClose={onClose} heading="Formulary" modalSize={style.custom_modal_size} custom_heading="pt-0">
      <div className={style.formularyBody}>
        {view === VIEW.LIST && (
          <>
            <FacilitySelect
              facilities={formularyFacilities}
              selectedFacility={selectedFacility}
              onChange={handleFacilityChange}
              loading={facilitiesLoading}
            />

            <div className={style.formularyMeta}>
              <h2 className={style.formularyName}>{productCategoryData?.formularyName ?? "—"}</h2>
              <span className={style.formularyUpdated}>
                Updated:{" "}
                <span className={style.updatedValue}>
                  {productCategoryData?.updatedDate ? dateOfBirthDate(productCategoryData?.updatedDate) : "-"}
                </span>
              </span>
            </div>

            <div className={style.divider} />

            <ProductDivisionChips productDivision={productDivision} selectedKeys={selectedChipKeys} onToggle={handleChipToggle} />

            <div className={style.divider} />

            <CategoryAccordion
              categories={categories}
              expandedKeys={expandedKeys}
              onToggle={handleAccordionToggle}
              onCardClick={handleCardClick}
              loading={searchLoading}
            />
          </>
        )}

        {view === VIEW.DETAIL && (
          <>
            {materialLoading && !selectedMaterial && <ProductDetailSkeleton />}

            {selectedMaterial && (
              <MaterialDetailView
                material={selectedMaterial}
                onBack={handleBack}
                onRequestMoreInfo={handleRequestMoreInfo}
                successMessage={reqSuccessMessage}
                loading={moreInfoLoading}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
